import React from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine,
} from 'recharts';
import { HolisticStudent } from '../../../types';
import { StatBox } from '../../../components/shared';

const AVG_HAND_SPAN_CM = 12.75;

const bmiColorMap: Record<string, string> = {
  'Underweight': 'text-amber-600',
  'Normal': 'text-emerald-600',
  'Overweight': 'text-orange-600',
  'Obese': 'text-red-600',
};

const bmiBgMap: Record<string, string> = {
  'Underweight': 'bg-amber-50 border-amber-200',
  'Normal': 'bg-emerald-50 border-emerald-200',
  'Overweight': 'bg-orange-50 border-orange-200',
  'Obese': 'bg-red-50 border-red-200',
};

// Estimated height reference for ~6 year old children.
const heightRef = [
  { label: 'Student', value: 0, fill: '#6366F1' },
  { label: 'Avg Height (6-yr)', value: 115, fill: '#94A3B8' },
];
const weightRef = [
  { label: 'Student', value: 0, fill: '#6366F1' },
  { label: 'Avg (Age)', value: 32, fill: '#94A3B8' },
];

const PhysicalTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();
  const p = student.physical;
  const handSpanCount = p.handSpanCount ?? +(p.height / AVG_HAND_SPAN_CM).toFixed(1);

  const heightData = [{ ...heightRef[0], value: p.height }, heightRef[1]];
  const weightData = [{ ...weightRef[0], value: p.weight }, weightRef[1]];

  return (
    <div className="space-y-6">
      {/* Physical Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Hand Span Count" value={`${handSpanCount}`} />
        <StatBox label="Estimated Height" value={`${p.height} cm`} />
        <StatBox label="Weight" value={`${p.weight} kg`} />
        <div className={`rounded-xl border p-4 shadow-sm ${bmiBgMap[p.bmiCategory]}`}>
          <p className="text-xs text-gray-500 mb-1">BMI</p>
          <p className={`text-xl font-bold ${bmiColorMap[p.bmiCategory]}`}>{p.bmi.toFixed(1)}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Estimated Height Comparison */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Estimated Height vs Age Average (cm)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={heightData} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 160]} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <ReferenceLine y={115} stroke="#94A3B8" strokeDasharray="3 3" label={{ value: 'Avg (6-yr)', position: 'right', fontSize: 11 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {heightData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Weight Comparison */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Weight vs Age Average</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={weightData} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 50]} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <ReferenceLine y={32} stroke="#94A3B8" strokeDasharray="3 3" label={{ value: 'Avg', position: 'right', fontSize: 11 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {weightData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* BMI + Hand Span Method */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">BMI Interpretation</h3>
        <p className="text-sm text-gray-600">
          Estimated height is calculated as: <span className="font-semibold">hand span count x 12.75 cm</span>
        </p>
        <p className="text-sm text-gray-600 mt-1">
          BMI category: <span className={`font-semibold ${bmiColorMap[p.bmiCategory]}`}>{p.bmiCategory}</span>
        </p>
      </div>
    </div>
  );
};

export default PhysicalTab;
